import React, { useCallback, useMemo, useState } from "react";
import { Dropdown, Menu, Tag, notification } from "antd";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsis, faEye, faPrint } from "@fortawesome/free-solid-svg-icons";

import SmartRtpTablePage from "./components/SmartRtpTablePage";
import { ORDER_STATUS_TABS } from "./config";
import { TZ } from "../../../config/constants";
import {
  getGameWiseReport,
  getGamesList,
  getOrdersList,
  setOrderRePrint,
} from "../../../controllers/V1/smartRtp/smartRtpController";

function OrderActionsMenu({ row, onRePrintToggle, updatingId }) {
  const navigate = useNavigate();
  const isUpdating = updatingId === row?._id;

  return (
    <Dropdown
      placement="bottomLeft"
      arrow
      overlay={
        <Menu>
          <Menu.Item
            key="view"
            onClick={() => navigate(`/smart-rtp/orders/details?editId=${row._id}`)}
          >
            <FontAwesomeIcon icon={faEye} /> <span className="show_span">View</span>
          </Menu.Item>
          {row.status === "Success" ? (
            <Menu.Item
              key="reprint"
              disabled={isUpdating}
              onClick={() => onRePrintToggle(row)}
            >
              <FontAwesomeIcon icon={faPrint} />{" "}
              <span className="show_span">
                {row.re_print === "Y" ? "Disable Re-print" : "Enable Re-print"}
              </span>
            </Menu.Item>
          ) : null}
        </Menu>
      }
    >
      <FontAwesomeIcon icon={faEllipsis} style={{ cursor: "pointer" }} />
    </Dropdown>
  );
}

function formatBahrainDateTime(value) {
  if (!value) return "-";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(new Date(value));
  const get = (type) => parts.find((part) => part.type === type)?.value || "";
  return `${get("day")}-${get("month")}-${get("year")} ${get("hour")}:${get("minute")}:${get("second")}`;
}

function formatOrderTickets(row) {
  const tickets = Array.isArray(row?.ticket_ids) ? row.ticket_ids : [];
  return tickets
    .map((ticket) =>
      Array.isArray(ticket?.numbers) ? ticket.numbers.join(", ") : ""
    )
    .filter(Boolean)
    .join(" | ");
}

function formatOrderDrawNumbers(row) {
  const drawNumbers = Array.isArray(row?.ticket_ids)
    ? row.ticket_ids.flatMap((ticket) => ticket?.match_result?.winning_numbers || [])
    : [];
  return [...new Set(drawNumbers)].join(", ");
}

export default function SmartRtpOrdersPage() {
  const [refreshToken, setRefreshToken] = useState(0);
  const [updatingId, setUpdatingId] = useState(null);

  const handleRePrintToggle = useCallback(async (row) => {
    if (!row?._id || row.status !== "Success") return;
    const nextFlag = row.re_print === "Y" ? "N" : "Y";
    setUpdatingId(row._id);
    try {
      const res = await setOrderRePrint({ editId: row._id, re_print: nextFlag });
      if (res?.status === true) {
        notification.success({
          message: nextFlag === "Y" ? "Re-print enabled" : "Re-print disabled",
          description: `Order ${row.order_no} re_print set to ${nextFlag}.`,
        });
        setRefreshToken((value) => value + 1);
      } else {
        notification.error({
          message: "Update failed",
          description: res?.message || "Unable to update re-print flag.",
        });
      }
    } finally {
      setUpdatingId(null);
    }
  }, []);

  const exportConfig = useMemo(
    () => ({
      showGameSelect: true,
      enableGameWiseReport: true,
      hideSearch: true,
      useGeneralDataExportTimes: true,
      loadGames: () =>
        getGamesList({
          type: "",
          condition: {},
          limit: 0,
          skip: 0,
          sort: { title: 1 },
        }),
      fetchGameWiseReport: (options) => getGameWiseReport(options),
    }),
    []
  );

  const searchOptions = useMemo(
    () => [
      { value: "order_no", label: "Order number" },
      { value: "seller.store_name", label: "Seller Store Name" },
      { value: "seller.mobile", label: "Seller Mobile" },
      { value: "seller.pos_number", label: "Seller POS" },
      { value: "game_title", label: "Campaign Title" },
    ],
    []
  );

  const columns = useMemo(
    () => [
      { key: "order_no", label: "Order No", showInExcel: true, showInOrderList: true },
      { key: "game_title", label: "Game", showInExcel: true, showInOrderList: true },
      {
        key: "ticket",
        label: "Ticket",
        showInExcel: true,
        showInOrderList: false,
        render: (row) => formatOrderTickets(row) || "-",
        exportValue: (row) => formatOrderTickets(row),
      },
      {
        key: "draw_number",
        label: "Draw Number",
        showInExcel: true,
        showInOrderList: false,
        render: (row) => formatOrderDrawNumbers(row) || "-",
        exportValue: (row) => formatOrderDrawNumbers(row),
      },
      {
        key: "seller_pos_number",
        label: "POS Number",
        showInExcel: true,
        showInOrderList: true,
        render: (row) =>
          row?.seller?.pos_number != null && row.seller.pos_number !== ""
            ? row.seller.pos_number
            : "-",
        exportValue: (row) => row?.seller?.pos_number ?? "",
      },
      {
        key: "seller_name",
        label: "Seller",
        showInExcel: true,
        showInOrderList: true,
        render: (row) => {
          if (!row?.seller) return "-";
          if(row?.seller?.store_name) return `${row?.seller?.store_name || ""}`.trim();
          return `${row?.seller?.first_name || ""} ${row?.seller?.last_name || ""}`.trim();
        },
        exportValue: (row) => {
          if (!row?.seller) return "";
          if(row?.seller?.store_name) return `${row?.seller?.store_name || ""}`.trim();
          return `${row?.seller?.first_name || ""} ${row?.seller?.last_name || ""}`.trim();
        },
      },
      {
        key: "bind_person_name",
        label: "Bind With",
        showInExcel: true,
        showInOrderList: false,
        render: (row) => row?.seller?.bind_person_name || "-",
        exportValue: (row) => row?.seller?.bind_person_name || "",
      },
      {
        key: "bind_person_mobile",
        label: "Bind Mobile",
        showInExcel: true,
        showInOrderList: false,
        render: (row) => row?.seller?.bind_person_mobile || "-",
        exportValue: (row) => row?.seller?.bind_person_mobile || "",
      },
      {
        key: "buyer_ref",
        label: "Buyer Ref",
        showInExcel: false,
        showInOrderList: true,
      },
      { key: "total_qty", label: "Tickets", showInExcel: true, showInOrderList: true },
      { key: "total_points", label: "Points", showInExcel: true, showInOrderList: true },
      {
        key: "prize",
        label: "Reward",
        showInExcel: true,
        showInOrderList: true,
        render: (row) => row?.prize_allocation?.total_reward || 0,
      },
      {
        key: "win",
        label: "Win",
        showInExcel: true,
        showInOrderList: true,
        render: (row) => (row?.prize_allocation?.allocated ? "Yes" : "No"),
      },
      { key: "status", label: "Status", showInExcel: true, showInOrderList: true },
      {
        key: "is_print",
        label: "Printed",
        showInExcel: true,
        showInOrderList: true,
        render: (row) => row?.is_print || "N",
      },
      {
        key: "re_print",
        label: "Re-print",
        showInExcel: true,
        showInOrderList: true,
        render: (row) =>
          row?.re_print === "Y" ? <Tag color="green">Y</Tag> : <Tag>N</Tag>,
        exportValue: (row) => row?.re_print || "N",
      },
      {
        key: "createdAt",
        label: "Created",
        showInExcel: true,
        showInOrderList: true,
        render: (row) => formatBahrainDateTime(row?.createdAt),
        exportValue: (row) =>
          row?.createdAt ? formatBahrainDateTime(row.createdAt) : "",
      },
      {
        key: "actions",
        label: "Actions",
        showInExcel: false,
        showInOrderList: true,
        render: (row) => (
          <OrderActionsMenu
            row={row}
            onRePrintToggle={handleRePrintToggle}
            updatingId={updatingId}
          />
        ),
      },
    ],
    [handleRePrintToggle, updatingId]
  );

  return (
    <SmartRtpTablePage
      title="Smart RTP Orders"
      activeKey="orders"
      fetchData={getOrdersList}
      statusTabs={ORDER_STATUS_TABS}
      searchFields={searchOptions.map((option) => option.value)}
      searchOptions={searchOptions}
      searchNumericFields={["seller.mobile", "seller.pos_number"]}
      exportFileName="Smart RTP Orders"
      exportConfig={exportConfig}
      refreshToken={refreshToken}
      columns={columns}
    />
  );
}
